/**
 * gsap-animations.js
 * Central GSAP animation helper — include once via stylesheet_tag equivalent,
 * then call window.GSAPAnim.* from any section's small inline script.
 *
 * Usage in a section:
 *   {{ 'gsap-animations.js' | asset_url | script_tag }}
 *   <script>
 *     document.addEventListener('gsap:ready', function () {
 *       GSAPAnim.fadeUp('.my-heading');
 *       GSAPAnim.staggerFadeUp('.my-card');
 *     });
 *   </script>
 */

(function () {
  var GSAP_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js';
  var ST_CDN   = 'https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/ScrollTrigger.min.js';

  function loadScript(src, cb) {
    var existing = document.querySelector('script[src="' + src + '"]');
    if (existing) { existing.addEventListener('load', cb); if (window.gsap) cb(); return; }
    var s = document.createElement('script');
    s.src = src;
    s.onload = cb;
    document.head.appendChild(s);
  }

  function boot() {
    loadScript(GSAP_CDN, function () {
      loadScript(ST_CDN, function () {
        gsap.registerPlugin(ScrollTrigger);

        /* Smoother defaults — hardware-accelerated */
        gsap.config({ force3D: true });
        ScrollTrigger.config({ ignoreMobileResize: true });

        window.GSAPAnim = {

          /**
           * Universal reveal with three cases based on element position at load time:
           *   1. Element is above the viewport (already scrolled past) → leave visible, no animation
           *   2. Element is currently in the viewport → animate immediately (no ScrollTrigger)
           *   3. Element is below the viewport → hide + ScrollTrigger animation on scroll
           *
           * opts: { start, fromY, fromX, duration, stagger, delay, ease, triggerEl }
           */
          reveal: function (targets, opts) {
            opts = opts || {};
            var els = typeof targets === 'string'
              ? Array.prototype.slice.call(document.querySelectorAll(targets))
              : (targets.length !== undefined ? Array.prototype.slice.call(targets) : [targets]);
            if (!els.length) return;

            var triggerEl = opts.triggerEl || els[0];
            if (!triggerEl) return;
            var fromY     = opts.fromY !== undefined ? opts.fromY : 50;
            var fromX     = opts.fromX !== undefined ? opts.fromX : 0;
            var start     = opts.start || 'top 75%';
            var vh        = window.innerHeight;
            var rect      = triggerEl.getBoundingClientRect();

            /* Case 1: completely above viewport — already scrolled past, leave as-is */
            if (rect.bottom < 0) return;

            els.forEach(function (el) { el.style.willChange = 'transform, opacity'; });
            gsap.set(els, { opacity: 0, y: fromY, x: fromX });

            /* Case 2: currently in the viewport — animate immediately (respect delay for sequencing) */
            if (rect.top < vh) {
              gsap.to(els, {
                opacity: 1, y: 0, x: 0,
                duration: opts.duration || 0.7,
                ease: opts.ease || 'power2.out',
                stagger: opts.stagger || 0,
                delay: opts.delay || 0,
                clearProps: 'willChange'
              });
              return;
            }

            /* Case 3: below the viewport — animate when scrolled into view */
            gsap.to(els, {
              opacity: 1, y: 0, x: 0,
              duration: opts.duration || 0.7,
              ease: opts.ease || 'power2.out',
              stagger: opts.stagger || 0,
              delay: opts.delay || 0,
              clearProps: 'willChange',
              scrollTrigger: { trigger: triggerEl, start: start, once: true }
            });
          },

          /* Convenience wrappers */
          fadeUp: function (target, opts) {
            var el = typeof target === 'string' ? document.querySelector(target) : target;
            this.reveal(el ? [el] : [], opts || {});
          },
          staggerFadeUp: function (targets, opts) {
            this.reveal(targets, opts || {});
          },
          slideInRight: function (target, opts) {
            opts = opts || {};
            opts.fromX = opts.x || 60; opts.fromY = 0;
            var el = typeof target === 'string' ? document.querySelector(target) : target;
            this.reveal(el ? [el] : [], opts);
          },
          slideInLeft: function (target, opts) {
            opts = opts || {};
            opts.fromX = -(opts.x || 60); opts.fromY = 0;
            var el = typeof target === 'string' ? document.querySelector(target) : target;
            this.reveal(el ? [el] : [], opts);
          }
        };

        /* Dispatch ready event so sections can register their triggers */
        document.dispatchEvent(new CustomEvent('gsap:ready'));

        /* After all handlers have registered, refresh ScrollTrigger so
           elements already in the viewport on load/refresh animate correctly */
        requestAnimationFrame(function () {
          ScrollTrigger.refresh();
        });
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
